import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Trash2, Undo2, Globe } from "lucide-react";

interface ClimateZoneRecord {
  iso_country_code: string;
  country_name: string;
  koppen_code: string;
  description?: string | null;
  region: 'Africa' | 'Central America' | 'Caribbean' | 'Other';
}

export const ClimateZonesImporter = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [csvData, setCsvData] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setCsvData(content);
      };
      reader.readAsText(selectedFile);
    }
  };

  const parseCsvData = (csvText: string): ClimateZoneRecord[] => {
    const lines = csvText.trim().split("\n");
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    
    // Helper function to generate ISO country code from country name
    const generateIsoCode = (countryName: string): string => {
      const codeMap: Record<string, string> = {
        'morocco': 'MA', 'algeria': 'DZ', 'tunisia': 'TN', 'libya': 'LY', 'egypt': 'EG',
        'mauritania': 'MR', 'mali': 'ML', 'niger': 'NE', 'chad': 'TD', 'sudan': 'SD',
        'ethiopia': 'ET', 'somalia': 'SO', 'kenya': 'KE', 'tanzania': 'TZ', 'uganda': 'UG',
        'democratic republic of congo': 'CD', 'central african republic': 'CF',
        'cameroon': 'CM', 'nigeria': 'NG', 'ghana': 'GH', 'ivory coast': 'CI',
        'senegal': 'SN', 'guinea': 'GN', 'sierra leone': 'SL', 'liberia': 'LR',
        'burkina faso': 'BF', 'togo': 'TG', 'benin': 'BJ', 'gabon': 'GA',
        'equatorial guinea': 'GQ', 'sao tome and principe': 'ST', 'cape verde': 'CV',
        'gambia': 'GM', 'guinea-bissau': 'GW', 'madagascar': 'MG', 'mauritius': 'MU',
        'seychelles': 'SC', 'comoros': 'KM', 'djibouti': 'DJ', 'eritrea': 'ER',
        'south africa': 'ZA', 'namibia': 'NA', 'botswana': 'BW', 'zimbabwe': 'ZW',
        'zambia': 'ZM', 'malawi': 'MW', 'mozambique': 'MZ', 'angola': 'AO',
        'rwanda': 'RW', 'burundi': 'BI', 'lesotho': 'LS', 'eswatini': 'SZ',
        'swaziland': 'SZ'
      };
      return codeMap[countryName.toLowerCase()] || countryName.substring(0, 2).toUpperCase();
    };
    
    const data: ClimateZoneRecord[] = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      // Handle CSV parsing with quoted values containing commas
      const row = lines[i];
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let j = 0; j < row.length; j++) {
        const char = row[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      let region: 'Africa' | 'Central America' | 'Caribbean' | 'Other' = 'Other';
      let countryName = '';
      let koppenCodes = '';
      let description = '';

      headers.forEach((header, idx) => {
        const val = values[idx];
        if (val === undefined || val === null || val === '') return;
        
        const cleanedVal = val.toString().trim().replace(/^"|"$/g, '');
        
        if (header.includes("region")) {
          const regionMap: Record<string, 'Africa' | 'Central America' | 'Caribbean' | 'Other'> = {
            'africa': 'Africa',
            'central america': 'Central America',
            'caribbean': 'Caribbean'
          };
          region = regionMap[cleanedVal.toLowerCase()] || 'Other';
        } else if (header.includes("country") || header === "country") {
          countryName = cleanedVal;
        } else if (header.includes("koppen") || header.includes("zones")) {
          koppenCodes = cleanedVal;
        } else if (header.includes("notes") || header.includes("description")) {
          description = cleanedVal || '';
        }
      });

      if (countryName && koppenCodes && region) {
        // Split multiple Köppen codes and create separate records
        const codes = koppenCodes.split(',').map(code => code.trim().replace(/^"|"$/g, ''));
        
        codes.forEach(koppenCode => {
          if (koppenCode && koppenCode.length > 0) {
            data.push({
              iso_country_code: generateIsoCode(countryName),
              country_name: countryName,
              koppen_code: koppenCode,
              description: description || null,
              region: region
            });
          }
        });
      }
    }

    return data;
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      toast({
        title: "Error",
        description: "Please paste CSV data or select a file first.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      const parsed = parseCsvData(csvData);
      if (parsed.length === 0) {
        toast({
          title: "Error",
          description: "No valid climate zone records found. Check headers: iso_country_code, country_name, koppen_code, region, description",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("import-climate-zones", {
        body: { data: parsed },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || `Imported ${parsed.length} climate zones successfully.`,
      });

      setCsvData("");
      setFile(null);
    } catch (err: any) {
      console.error("Climate zones import error:", err);
      toast({
        title: "Error",
        description: `Failed to import climate zones: ${err?.message || err}`,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleUndoLastImport = async () => {
    setIsUndoing(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-climate-zones", {
        body: { action: "undo_last" },
        method: "DELETE"
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "Last climate zones import has been undone.",
      });
    } catch (error: any) {
      console.error("Undo climate zones error:", error);
      toast({
        title: "Error",
        description: `Failed to undo last import: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUndoing(false);
    }
  };

  const handleClearAllClimateZones = async () => {
    if (!confirm("Are you sure you want to delete ALL climate zones? This action cannot be undone.")) {
      return;
    }

    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke("import-climate-zones", {
        body: { action: "clear_all" },
        method: "DELETE"
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "All climate zones have been cleared successfully.",
      });
    } catch (error: any) {
      console.error('Clear climate zones error:', error);
      toast({
        title: "Error",
        description: `Failed to clear climate zones: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Climate Zones Importer
        </CardTitle>
        <CardDescription>
          Import Köppen climate zone data from CSV. Expected columns: iso_country_code, country_name, koppen_code, region, description
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Upload CSV File</label>
          <Input type="file" accept=".csv,.txt" onChange={handleFileChange} className="mb-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Or Paste CSV Data</label>
          <Textarea
            placeholder="Paste your CSV data here..."
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            rows={10}
            className="font-mono text-sm"
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={handleImport} disabled={isImporting || !csvData.trim()} className="flex-1">
            {isImporting ? "Importing..." : "Import Climate Zones"}
          </Button>

          <Button 
            onClick={handleUndoLastImport}
            disabled={isUndoing}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <Undo2 className="h-4 w-4" />
            {isUndoing ? "Undoing..." : "Undo Last"}
          </Button>

          <Button 
            onClick={handleClearAllClimateZones}
            disabled={isClearing}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isClearing ? "Clearing..." : "Clear All"}
          </Button>
        </div>

        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>CSV Format Examples:</strong>
          <br />
          <strong>Format 1:</strong> iso_country_code,country_name,koppen_code,region,description
          <br />
          <strong>Format 2:</strong> Region,Country,Primary_Koppen_Zones,Notes
          <br />
          Africa,Morocco,"Csa,Csb,BSk",Mediterranean climate zones
        </div>
      </CardContent>
    </Card>
  );
};